import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '@/components/SeoHead';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import { Button } from '@/components/ui/button';
import { useHydrated } from '@/hooks/useHydrated';
import { useSignupEntityType, useSignupUrl } from '@/hooks/useSignupUrl';
import { useTranslation } from '@/i18n/i18n';
import { buildSignUpUrl } from '@/lib/auth-url';
import { trackLandingCtaClicked } from '@/lib/analytics';
import {
  GUIDES,
  PRZEWODNIKI_BASE_PATH,
  guideUrl,
  type Guide,
  type GuideFunnelCta,
  type GuideTable,
} from '@/content/przewodniki';

const REVIEW_DATE_FORMAT = new Intl.DateTimeFormat('pl-PL', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/**
 * Render `Guide.dateModified` (an ISO date) for Polish readers.
 *
 * The raw ISO string stays in the `<time dateTime>` attribute for machines;
 * only the visible text is localised. Parsed at local midday, not midnight:
 * `new Date('2026-08-05')` is UTC midnight, which formats as the PREVIOUS day
 * in any negative-offset timezone.
 */
function formatReviewDate(isoDate: string): string {
  const parsed = new Date(`${isoDate}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? isoDate : REVIEW_DATE_FORMAT.format(parsed);
}

/**
 * The CTA's own campaign UTMs are static, so the prerender renders them; the
 * visitor's first-touch attribution lives in browser storage and joins after
 * hydration (#9059 r2), or the hydrated link would keep the prerendered href.
 */
function useCampaignSignupUrl(cta: GuideFunnelCta | undefined, slot: string) {
  const { locale } = useTranslation();
  const entityType = useSignupEntityType();
  const hydrated = useHydrated();

  return useMemo(() => {
    if (!cta) return undefined;
    return buildSignUpUrl(
      locale,
      `?utm_source=site&utm_medium=cta&utm_campaign=${cta.utmCampaign}&utm_content=${cta.utmContent}`,
      { entityType, source: `explainer_${slot}`, firstTouch: hydrated }
    );
  }, [cta, entityType, locale, hydrated, slot]);
}

function GuideSignupButton({
  cta,
  href,
  location,
  className,
}: {
  cta: GuideFunnelCta;
  href: string;
  location: string;
  className?: string;
}) {
  return (
    <Button asChild className={className ?? 'rounded-full font-semibold'}>
      <a
        href={href}
        onClick={() => trackLandingCtaClicked(location, cta.label, { campaign: cta.utmCampaign })}
      >
        {cta.label}
        <ArrowRight className="size-4" aria-hidden="true" />
      </a>
    </Button>
  );
}

export function ComparisonTable({ table, sectionId }: { table: GuideTable; sectionId: string }) {
  return (
    <div className="mt-6">
      {table.caption ? (
        <p className="mb-3 text-sm font-medium text-foreground">{table.caption}</p>
      ) : null}

      {/* Mobile: stacked cards — no horizontal discovery on a 320px screen. */}
      <div className="space-y-3 md:hidden">
        {table.rows.map((row, rowIndex) => (
          <article
            key={`${sectionId}-card-${rowIndex}`}
            className="rounded-xl border border-border/60 bg-card p-4 shadow-sm"
          >
            <h3 className="text-base font-semibold text-foreground">{row[0]}</h3>
            <dl className="mt-3 space-y-2">
              {table.headers.slice(1).map((header, headerIndex) => (
                <div key={`${sectionId}-card-${rowIndex}-${headerIndex}`}>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {header}
                  </dt>
                  <dd className="mt-0.5 text-sm text-foreground/80 leading-relaxed">
                    {row[headerIndex + 1]}
                  </dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
          {table.caption ? <caption className="sr-only">{table.caption}</caption> : null}
          <thead>
            <tr className="border-b border-border">
              {table.headers.map((header) => (
                <th
                  key={`${sectionId}-th-${header}`}
                  scope="col"
                  className="px-3 py-3 font-semibold text-foreground align-bottom"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr
                key={`${sectionId}-tr-${rowIndex}`}
                className="border-b border-border/50 align-top"
              >
                {row.map((cell, cellIndex) => {
                  const Cell = cellIndex === 0 ? 'th' : 'td';
                  return (
                    <Cell
                      key={`${sectionId}-td-${rowIndex}-${cellIndex}`}
                      scope={cellIndex === 0 ? 'row' : undefined}
                      className={
                        cellIndex === 0
                          ? 'px-3 py-3 font-semibold text-foreground'
                          : 'px-3 py-3 text-foreground/80 leading-relaxed'
                      }
                    >
                      {cell}
                    </Cell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Shared layout for the /przewodniki/* explainer pages (AI-SEO pack).
 *
 * Three deliberate choices:
 *
 * 1. **Polish-only, canonical to the unprefixed URL.** The /en and /uk routes
 *    render the same Polish article rather than 404ing, and every locale emits
 *    <link rel="canonical"> pointing at the Polish URL — so the duplicate never
 *    competes with itself in the index. This mirrors the existing partial-i18n
 *    reality on this site; it is not an oversight.
 *
 * 2. **JSON-LD lives ONLY in the static HTML entry, not here.** Every
 *    /przewodniki/* URL is rewritten by vercel.json to that guide's own
 *    przewodnik-*.html, so a crawler or AI fetcher always receives Article +
 *    FAQPage + BreadcrumbList without executing a line of JS. Emitting the same
 *    blocks from Helmet too would duplicate all three on every direct load
 *    (verified in the dev server, 2026-08-05) — and a page carrying two FAQPage
 *    blocks is a rich-results liability, not a belt-and-braces win. The blocks
 *    are still BUILT from src/lib/jsonld.ts; src/test/przewodniki.test.tsx
 *    asserts the static entries equal the builder output exactly, so they
 *    cannot drift from the visible copy. Client-side navigation is the only
 *    path that lands here without the guide's own JSON-LD — and only humans
 *    navigate that way.
 *
 * 3. **Lead paragraph first, before any chrome.** AI answer engines lift the
 *    first coherent block that answers the query. Everything above it is noise
 *    competing for that slot.
 */

export function ExplainerLayout({ guide }: { guide: Guide }) {
  const signupUrl = useSignupUrl({ source: 'explainer' });
  const canonical = guideUrl(guide.slug);
  const siblings = GUIDES.filter((other) => other.slug !== guide.slug);
  const earlySignupUrl = useCampaignSignupUrl(guide.earlyCta, 'early');
  const mentionSignupUrl = useCampaignSignupUrl(guide.mentionCta, 'mention');
  const footerSignupUrl = useCampaignSignupUrl(guide.footerCta, 'footer');
  const stickySignupUrl = useCampaignSignupUrl(guide.stickyCta, 'sticky');
  const mentionCta = guide.mentionCta;

  return (
    <div className={`min-h-screen pt-16 md:pt-20${guide.stickyCta ? ' pb-24 md:pb-0' : ''}`}>
      <SeoHead>
        {/* The article body is Polish in every locale route — declare it honestly. */}
        <html lang="pl" />
        <title>{guide.seoTitle}</title>
        <meta name="description" content={guide.description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={guide.seoTitle} />
        <meta property="og:description" content={guide.description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="pl_PL" />
        {/* No JSON-LD here on purpose — see note 2 in the header comment. */}
      </SeoHead>

      <Navigation />

      <article className="bg-background">
        {/* Breadcrumb */}
        <nav
          aria-label="Ścieżka nawigacji"
          className="max-w-3xl mx-auto px-6 md:px-8 pt-10 text-sm text-muted-foreground"
        >
          <ol className="flex flex-wrap items-center gap-1.5">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">
                Bookeper
              </Link>
            </li>
            <li aria-hidden="true" role="presentation">
              <ChevronRight className="size-3.5" />
            </li>
            {/* `Przewodniki` is a grouping label, not a page — there is no
                /przewodniki index route — so it must not claim aria-current.
                The current page is the guide itself. */}
            <li className="text-muted-foreground">Przewodniki</li>
            <li aria-hidden="true" role="presentation">
              <ChevronRight className="size-3.5" />
            </li>
            <li aria-current="page" className="text-foreground">
              {/* `navLabel` is optional on Guide and documented as falling back
                  to `h1` — honour that here or a guide without one renders an
                  empty final crumb. */}
              {guide.navLabel ?? guide.h1}
            </li>
          </ol>
        </nav>

        {/* Lead — the AI-quotable block. Nothing between the H1 and it. */}
        <header className="max-w-3xl mx-auto px-6 md:px-8 pt-6 pb-10">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
            {guide.h1}
          </h1>
          <p className="text-lg md:text-xl text-foreground/80 leading-relaxed">{guide.lead}</p>
          {guide.productLimit ? (
            <p className="mt-4 text-base text-foreground/80 leading-relaxed">
              {guide.productLimit}
            </p>
          ) : null}
          {guide.earlyCta && earlySignupUrl ? (
            <div className="mt-6 rounded-xl border border-border/60 bg-card p-5">
              <GuideSignupButton
                cta={guide.earlyCta}
                href={earlySignupUrl}
                location={`${guide.slug}_lead_inline`}
              />
              {guide.earlyCta.priceLine ? (
                <p className="mt-3 text-sm font-medium text-foreground">
                  {guide.earlyCta.priceLine}
                </p>
              ) : null}
              {guide.earlyCta.microcopy ? (
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {guide.earlyCta.microcopy}
                </p>
              ) : null}
            </div>
          ) : null}
          <p className="mt-6 text-sm text-muted-foreground">
            Ostatnia weryfikacja faktów:{' '}
            <time dateTime={guide.dateModified}>{formatReviewDate(guide.dateModified)}</time>
          </p>
        </header>

        {/* Table of contents — question-led, so it doubles as an answer index. */}
        <nav aria-label="Spis treści" className="max-w-3xl mx-auto px-6 md:px-8 pb-12">
          <div className="rounded-xl border border-border/60 bg-card p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              W tym przewodniku
            </h2>
            <ul className="space-y-2 text-sm">
              {guide.sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-foreground hover:text-primary transition-colors"
                  >
                    {section.heading}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 md:px-8 pb-8 space-y-12">
          {guide.sections.map((section) => (
            <section key={section.id} id={section.id} aria-labelledby={`${section.id}-heading`}>
              <h2
                id={`${section.id}-heading`}
                className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
              >
                {section.heading}
              </h2>
              <div className="space-y-4 text-base md:text-lg text-foreground/80 leading-relaxed">
                {/* Index keys, not a content slice: two paragraphs in a section
                    can share their first 40 characters, and these lists are
                    static — never reordered, inserted into, or filtered. */}
                {section.body.map((paragraph, index) => (
                  <p key={`${section.id}-p${index}`}>{paragraph}</p>
                ))}
              </div>
              {section.table ? (
                <ComparisonTable table={section.table} sectionId={section.id} />
              ) : null}
              {section.subsections
                ? section.subsections.map((subsection, subsectionIndex) => (
                    <div key={`${section.id}-sub-${subsectionIndex}`} className="mt-6">
                      <h3 className="text-xl font-semibold tracking-tight mb-3">
                        {subsection.heading}
                      </h3>
                      <div className="space-y-3 text-base md:text-lg text-foreground/80 leading-relaxed">
                        {subsection.body.map((paragraph, paragraphIndex) => (
                          <p key={`${section.id}-sub-${subsectionIndex}-p${paragraphIndex}`}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))
                : null}
              {section.bullets ? (
                <ul className="mt-5 space-y-2.5">
                  {section.bullets.map((bullet, index) => (
                    <li
                      key={`${section.id}-b${index}`}
                      className="flex gap-3 text-base text-foreground/80"
                    >
                      <Check
                        className="size-5 text-primary flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </div>

        {/* FAQ — the visible twin of the FAQPage JSON-LD in the static entry.
            Both are generated from guide.faq, so they cannot disagree. */}
        <section aria-labelledby="faq-heading" className="max-w-3xl mx-auto px-6 md:px-8 py-12">
          <h2 id="faq-heading" className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
            Najczęstsze pytania
          </h2>
          <div className="space-y-6">
            {guide.faq.map((item) => (
              <div key={item.q} className="rounded-xl border border-border/60 bg-card p-5">
                <h3 className="text-lg font-semibold text-foreground mb-2">{item.q}</h3>
                <p className="text-base text-foreground/80 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Honest product mention — one block, informative, clearly labelled. */}
        <section
          aria-labelledby="bookeper-heading"
          className="max-w-3xl mx-auto px-6 md:px-8 pb-12"
        >
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-6">
            <h2 id="bookeper-heading" className="text-xl font-semibold mb-3">
              Co w tym temacie robi Bookeper
            </h2>
            <p className="text-base text-foreground/80 leading-relaxed">{guide.bookeper}</p>
            <div className="mt-5">
              {mentionCta && mentionSignupUrl ? (
                <GuideSignupButton
                  cta={mentionCta}
                  href={mentionSignupUrl}
                  location={`${guide.slug}_bookeper_mention`}
                />
              ) : (
                <Button asChild className="rounded-xl font-semibold">
                  <a href={signupUrl}>
                    Załóż konto
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Sources — the difference between a citable page and an opinion. */}
        <section aria-labelledby="sources-heading" className="max-w-3xl mx-auto px-6 md:px-8 pb-12">
          <h2
            id="sources-heading"
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3"
          >
            Na czym opieramy daty i liczby
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
            {guide.sources.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            {guide.disclaimer ??
              'Przewodnik ma charakter informacyjny i nie jest poradą podatkową. Terminy bywają przesuwane — przed decyzją potwierdź je w aktualnych materiałach Ministerstwa Finansów.'}
          </p>
        </section>

        {guide.footerCta && footerSignupUrl ? (
          <section
            aria-labelledby="guide-footer-cta-heading"
            className="max-w-3xl mx-auto px-6 md:px-8 pb-12"
          >
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-6">
              {guide.footerCta.heading ? (
                <h2
                  id="guide-footer-cta-heading"
                  className="text-xl md:text-2xl font-semibold mb-3"
                >
                  {guide.footerCta.heading}
                </h2>
              ) : (
                <h2 id="guide-footer-cta-heading" className="sr-only">
                  {guide.footerCta.label}
                </h2>
              )}
              {guide.footerCta.body ? (
                <p className="text-base text-foreground/80 leading-relaxed mb-5">
                  {guide.footerCta.body}
                </p>
              ) : null}
              <GuideSignupButton
                cta={guide.footerCta}
                href={footerSignupUrl}
                location={`${guide.slug}_footer`}
              />
            </div>
          </section>
        ) : null}

        {guide.relatedLinks?.length ? (
          <section
            aria-labelledby="related-heading"
            className="max-w-3xl mx-auto px-6 md:px-8 pb-12"
          >
            <h2 id="related-heading" className="text-xl font-semibold mb-5">
              Powiązane materiały
            </h2>
            <ul className="space-y-3">
              {guide.relatedLinks.map((link) => (
                <li key={link.href}>
                  {link.href.startsWith('/kalkulatory/') || link.href.startsWith('/blog/') ? (
                    <a
                      href={link.href}
                      className="group flex items-center justify-between p-5 rounded-xl border border-border/50 bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300"
                    >
                      <span className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
                        {link.label}
                      </span>
                      <ArrowRight
                        className="size-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="group flex items-center justify-between p-5 rounded-xl border border-border/50 bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300"
                    >
                      <span className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
                        {link.label}
                      </span>
                      <ArrowRight
                        className="size-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Sibling guides */}
        <section
          aria-labelledby="siblings-heading"
          className="max-w-3xl mx-auto px-6 md:px-8 pb-20"
        >
          <h2 id="siblings-heading" className="text-xl font-semibold mb-5">
            Pozostałe przewodniki
          </h2>
          <div className="space-y-3">
            {siblings.map((sibling) => (
              <Link
                key={sibling.slug}
                to={`${PRZEWODNIKI_BASE_PATH}/${sibling.slug}`}
                className="group flex items-center justify-between p-5 rounded-xl border border-border/50 bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300"
              >
                <span className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
                  {sibling.navLabel ?? sibling.h1}
                </span>
                <ArrowRight
                  className="size-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 ml-4"
                  aria-hidden="true"
                />
              </Link>
            ))}
            <Link
              to="/"
              className="group flex items-center justify-between p-5 rounded-xl border border-border/50 bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300"
            >
              <span className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
                Wróć na stronę główną Bookeper
              </span>
              <ArrowRight
                className="size-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 ml-4"
                aria-hidden="true"
              />
            </Link>
          </div>
        </section>
      </article>

      <Footer />
      {guide.stickyCta && stickySignupUrl ? (
        <div
          className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background/95 backdrop-blur-sm px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-lg md:hidden"
          data-testid="guide-sticky-cta"
        >
          <div className="flex items-center justify-between gap-3 max-w-3xl mx-auto">
            <p className="text-sm font-medium text-foreground leading-snug">
              {guide.stickyCta.body}
            </p>
            <GuideSignupButton
              cta={guide.stickyCta}
              href={stickySignupUrl}
              location={`${guide.slug}_sticky`}
              className="rounded-full font-semibold shrink-0"
            />
          </div>
        </div>
      ) : null}
      <CookieConsent />
    </div>
  );
}
